/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, useContext} from 'react'
import PropTypes from 'prop-types'
import {IntlProvider} from 'react-intl'
import {Helmet} from 'react-helmet'
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url'
import {useHistory, useLocation} from 'react-router-dom'
import {useQuery, useQueries} from '@tanstack/react-query'

import {
    Box,
    Center,
    Fade,
    Spinner,
    useDisclosure,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'
// HOCs
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'

import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    useAccessToken,
    useCategory,
    useShopperBasketsMutation,
    useCommerceApi,
    useCustomerType,
    useCustomerBaskets
} from '@salesforce/commerce-sdk-react'
import * as queryKeyHelpers from '@salesforce/commerce-sdk-react/hooks/ShopperProducts/queryKeyHelpers'
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import CheckoutFooter from '@salesforce/retail-react-app/app/pages/checkout/partials/checkout-footer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useUpdateShopperContext} from '@salesforce/retail-react-app/app/hooks/use-update-shopper-context'
import AboveHeader from '@salesforce/retail-react-app/app/components/_app/partials/above-header'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {getTargetLocale, fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import {SegmentContext} from '../../integrations/segment/app/contexts'
import useSegment from '../../integrations/segment/app/hooks/use-segment'

import Header from '../header'
import Footer from '../footer'
import OfflineBoundary from '../offline-boundary'
import CookieConsent from '../cookie-consent'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import CustomHeader from '../../pages/checkout/partials/custom-header'
import {ListMenu, ListMenuContent} from '../list-menu'
import {MobileListMenu, MobileListMenuContent} from '../mobile-list-menu'
import CustomFooter from '../../pages/checkout/partials/custom-footer'
import {HideOnDesktop, HideOnMobile} from '../responsive'
import CountDownTimerGlobalContentSlotHtml from '../content-slot/countDownTimer-global-content-slot-html'
import '../../styles/fonts.css'

import {
    watchOnlineStatus,
    flatten,
    isServer,
    mergeMatchedItems
} from '@salesforce/retail-react-app/app/utils/utils'
import {getPageType, createCookie} from '../../utils/custom-utils'
import {
    DEFAULT_SITE_TITLE,
    HOME_HREF,
    THEME_COLOR,
    CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
    CAT_MENU_DEFAULT_ROOT_CATEGORY,
    DEFAULT_LOCALE,
    ACTIVE_DATA_ENABLED
} from '@salesforce/retail-react-app/app/constants'

const onClient = typeof window !== 'undefined'
const themeName = process.env.CUSTOM_THEME

const PlaceholderComponent = () => (
    <Center p="2">
        <Spinner size="lg" color="primaryColor" />
    </Center>
)

/*
The categories tree can be really large! For performance reasons,
we only load the level 0 categories on server side, and load the rest
on client side to reduce SSR page size.
*/
const useLazyLoadCategories = () => {
    const itemsKey = 'categories'

    const levelZeroCategoriesQuery = useCategory({
        parameters: {id: CAT_MENU_DEFAULT_ROOT_CATEGORY, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH}
    })
    const filteredOnlineCategories = levelZeroCategoriesQuery?.data?.[itemsKey]?.filter(
        (cat) => cat?.c_showInMenu
    )
    const ids = filteredOnlineCategories?.map((category) => category.id)
    const queries = useCategoryBulk(ids, {
        enabled: onClient && ids?.length > 0
    })
    const dataArray = queries.map((query) => query.data).filter(Boolean)
    const isLoading = queries.some((query) => query.isLoading)
    const isError = queries.some((query) => query.isError)
    return {
        isLoading,
        isError,
        data: {
            ...levelZeroCategoriesQuery.data,
            [itemsKey]: mergeMatchedItems(filteredOnlineCategories || [], dataArray)
        }
    }
}

const DrawerMenuItemWithData = withCommerceSdkReact(
    ({itemComponent: ItemComponent, data, ...rest}) => (
        <Fade in={true}>
            <ItemComponent {...rest} item={data} itemComponent={DrawerMenuItemWithData} />
        </Fade>
    ),
    {
        hook: useCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id
            }
        }),
        placeholder: PlaceholderComponent
    }
)

const MobileListMenuContentWithData = withCommerceSdkReact(
    ({data, ...rest}) => (
        <Fade in={true}>
            <MobileListMenuContent {...rest} item={data} />
        </Fade>
    ),
    {
        hook: useCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id,
                levels: 2
            }
        }),
        placeholder: PlaceholderComponent
    }
)

const ListMenuContentWithData = withCommerceSdkReact(
    ({data, ...rest}) => (
        <Fade in={true}>
            <ListMenuContent {...rest} item={data} />
        </Fade>
    ),
    {
        hook: useCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id,
                levels: 2
            }
        }),
        placeholder: PlaceholderComponent
    }
)

const App = (props) => {
    const {children} = props
    const configs = getConfig()
    const {data: categoriesTree} = useLazyLoadCategories()
    const categories = flatten(categoriesTree || {}, 'categories')
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const activeData = useActiveData()
    const history = useHistory()
    const location = useLocation()
    const authModal = useAuthModal()
    const {isRegistered} = useCustomerType()
    const {site, locale, buildUrl} = useMultiSite()
    const styles = useStyleConfig('App')
    const {onClose} = useDisclosure()
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const segment = useSegment()
    const {isSegmentReady} = useContext(SegmentContext)
    const prevUrlRef = useRef(null)

    // state variable and constants at gloabl
    const [isOnline, setIsOnline] = useState(true)
    const hoverDelay = getcustomPreferencevalue('pwaNavMinicartHoverDelay')
    const isQuantamMetricsEnabeled = getcustomPreferencevalue('pwaQuantumMetricsEnabled')
    const quantumMetricsScript = configs.app.library.quantumMetricsScript
    const isMarketingPersonalizationEnabled = getcustomPreferencevalue(
        'pwaMarketingCloudPersonalizationEnabled'
    )
    const oneTrust = getcustomPreferencevalue('pwaEvidononetrust')
    const marketingPersonalizationScript = configs.app.library.marketingCloudPersonalizationScript
    const alias = configs.app.library.sfccAlias

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale, appOrigin)
        },
        enabled: isServer
    })

    // Used to conditionally render header/footer for checkout page
    const isCheckout = /\/checkout(?:\/(stripe|adyen))?$/.test(location.pathname)
    const isCustomHeader = /\/checkout(?:\/(stripe-confirm))?$/.test(location.pathname)

    // used condtionally to load power reviews script on required pages.
    const pageType = getPageType()
    const laodPRScript = onClient && pageType !== 'others'

    if (onClient) {
        const urlParams = new URLSearchParams(location.search)
        const src = urlParams.get('src')
        const utm_source = urlParams.get('utm_source')
        const utm_medium = urlParams.get('utm_medium')
        const utm_campaign = urlParams.get('utm_campaign')
        const utm_term = urlParams.get('utm_term')
        const utm_content = urlParams.get('utm_content')
        const irclickid = urlParams.get('irclickid')

        if (src) localStorage.setItem('sourceCode', src)
        if (utm_source) createCookie('utm_source', utm_source, 30)
        if (utm_medium) createCookie('utm_medium', utm_medium, 30)
        if (utm_campaign) createCookie('utm_campaign', utm_campaign, 30)
        if (utm_term) createCookie('utm_term', utm_term, 30)
        if (utm_content) createCookie('utm_content', utm_content, 30)
        if (irclickid) createCookie('irClickID', irclickid, 30)
    }

    // Get the current currency to be used through out the app
    const {l10n} = site
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId: customer.customerId}},
        {enabled: !!customer.customerId && !isServer}
    )
    const {data: basket} = useCurrentBasket()
    const createBasket = useShopperBasketsMutation('createBasket')
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const handleMenuClick = () => {
        setIsMenuOpen(!isMenuOpen)
    }

    const [isCountryModalOpen, setIsCountryModalOpen] = useState(false)
    const openCountryModal = () => setIsCountryModalOpen(true)
    const closeCountryModal = () => setIsCountryModalOpen(false)

    useEffect(() => {
        // Close the menu when location changes
        setIsMenuOpen(false)
    }, [location])

    useEffect(() => {
        if (!isSegmentReady) return

        const triggerPageEvent = async () => {
            if (document.readyState !== 'complete') {
                await new Promise((resolve) => {
                    window.addEventListener('load', resolve, {once: true})
                })
            }
            segment.pageEvent(prevUrlRef?.current)
            prevUrlRef.current = window?.location?.href
        }
        // segment page event
        triggerPageEvent()
    }, [location, isSegmentReady])

    useEffect(() => {
        // Create a new basket if the current customer doesn't have one.
        if (baskets?.total === 0) {
            createBasket.mutate({
                body: {}
            })
        }
    }, [baskets])

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    // Handle updating the shopper context
    useUpdateShopperContext()

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    }, [location])

    const onLogoClick = () => {
        // Goto the home page.
        const path = buildUrl(HOME_HREF)

        history.push(path)

        // Close the drawer.
        onClose()
    }

    const onCartClick = () => {
        const path = buildUrl('/cart')
        history.push(path)
    }

    const onAccountClick = () => {
        // Link to account page for registered customer, open auth modal otherwise
        if (isRegistered) {
            const path = buildUrl('/account')
            history.push(path)
        } else {
            // if they already are at the login page, do not show login modal
            if (new RegExp(`^/login$`).test(location.pathname)) return
            authModal.onOpen()
        }
    }

    const onWishlistClick = () => {
        // Link to wishlist page if registered; Header component will show auth modal for guest users
        const path = isRegistered ? buildUrl('/account/wishlist') : buildUrl('/guest/wishlist')
        history.push(path)
    }

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
    }, [location])

    const [isHoveringHeader, setIsHoveringHeader] = useState(false)

    return (
        <Box className={`sf-app ${themeName}`} {...styles.container}>
            <StorefrontPreview getToken={getTokenWhenReady}>
                <Helmet>
                    {/* <link rel="dns-prefetch" href="http://ui.powerreviews.com" />
                    <link rel="preconnect" href="http://ui.powerreviews.com" />
                    <link rel="dns-prefetch" href="https://maps.googleapis.com" />
                    <link rel="preconnect" href="https://maps.googleapis.com" /> */}

                    {laodPRScript && (
                        <script
                            src="http://ui.powerreviews.com/stable/4.0/ui.js"
                            type="text/javascript"
                            defer
                        />
                    )}

                    {isQuantamMetricsEnabeled && (
                        <script src={quantumMetricsScript} type="text/javascript" async />
                    )}

                    {isMarketingPersonalizationEnabled && (
                        <script src={marketingPersonalizationScript} type="text/javascript" async />
                    )}

                    {ACTIVE_DATA_ENABLED && (
                        <script
                            src={getAssetUrl('static/head-active_data.js')}
                            id="headActiveData"
                            type="text/javascript"
                        ></script>
                    )}
                </Helmet>
                <IntlProvider
                    onError={(err) => {
                        if (!messages) {
                            // During the ssr prepass phase the messages object has not loaded, so we can suppress
                            // errors during this time.
                            return
                        }
                        if (err.code === 'MISSING_TRANSLATION') {
                            // NOTE: Remove the error for missing translations during development,
                            // as we knew translations would be added later.
                            logger.warn('Missing translation', {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message
                                }
                            })
                            return
                        }
                        throw err
                    }}
                    locale={targetLocale}
                    messages={messages}
                    // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                    // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                    // - "extract-default-translations"
                    // - "compile-translations:pseudo"
                    defaultLocale={DEFAULT_LOCALE}
                >
                    <CurrencyProvider currency={currency}>
                        <Seo>
                            <meta name="theme-color" content={THEME_COLOR} />
                            <meta name="apple-mobile-web-app-title" content={DEFAULT_SITE_TITLE} />
                            <link rel="icon" href={getAssetUrl('static/ico/favicon.ico')} />
                            <link rel="manifest" href={getAssetUrl('static/manifest.json')} />
                            {/* 404 hence commented <link rel="stylesheet" href={getAssetUrl('main.css')} /> */}

                            {/* Urls for all localized versions of this page (including current page). For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
                            {site.l10n?.supportedLocales.map((locale) => (
                                <link
                                    rel="alternate"
                                    hrefLang={locale.id.toLowerCase()}
                                    href={`${alias}${getPathWithLocale(locale.id, buildUrl, {
                                        location: {
                                            ...location,
                                            search: ''
                                        }
                                    })}`}
                                    key={locale.id}
                                />
                            ))}
                            {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
                            <link
                                rel="alternate"
                                hrefLang={site.l10n.defaultLocale.slice(0, 2)}
                                href={`${alias}${getPathWithLocale(locale.id, buildUrl, {
                                    location: {
                                        ...location,
                                        search: ''
                                    }
                                })}`}
                            />
                            {/* A wider fallback for user locales that the app does not support */}
                            <link rel="alternate" hrefLang="x-default" href={`${appOrigin}/`} />

                            {/* canonical */}
                            <link
                                rel="canonical"
                                href={`${alias}${getPathWithLocale(locale.id, buildUrl, {
                                    location: {
                                        ...location,
                                        search: ''
                                    }
                                })}`}
                            ></link>
                        </Seo>
                        <ScrollToTop />
                        <Box id="app" display="flex" flexDirection="column" flex={1}>
                            <SkipNavLink zIndex="skipLink">Skip to Content</SkipNavLink>
                            <Box {...styles.headerWrapper} className="cr-header">
                                {!isCheckout && !isCustomHeader ? (
                                    <>
                                        <AboveHeader />
                                        <Header
                                            onMenuClick={handleMenuClick}
                                            isMenuOpen={isMenuOpen}
                                            onLogoClick={onLogoClick}
                                            onMyCartClick={onCartClick}
                                            onMyAccountClick={onAccountClick}
                                            onWishlistClick={onWishlistClick}
                                            isCountryModalOpen={isCountryModalOpen}
                                            openCountryModal={openCountryModal}
                                            closeCountryModal={closeCountryModal}
                                            hoverDelay={hoverDelay}
                                            onMouseEnter={() => setIsHoveringHeader(true)}
                                            onMouseLeave={() => setIsHoveringHeader(false)}
                                        >
                                            <HideOnDesktop isOpen={isMenuOpen}>
                                                <MobileListMenu
                                                    root={
                                                        categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                                                    }
                                                    itemsKey="categories"
                                                    itemsCountKey="onlineSubCategoriesCount"
                                                    contentComponent={MobileListMenuContentWithData}
                                                    isOpen={isMenuOpen}
                                                    handleMenuClick={handleMenuClick}
                                                />
                                            </HideOnDesktop>

                                            <HideOnMobile>
                                                <ListMenu
                                                    root={
                                                        categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                                                    }
                                                    itemsKey="categories"
                                                    itemsCountKey="onlineSubCategoriesCount"
                                                    contentComponent={ListMenuContentWithData}
                                                    hoverDelay={hoverDelay}
                                                />
                                            </HideOnMobile>
                                        </Header>
                                    </>
                                ) : !isCheckout && isCustomHeader ? (
                                    <CustomHeader />
                                ) : (
                                    <CheckoutHeader />
                                )}
                            </Box>
                            <CountDownTimerGlobalContentSlotHtml
                                slotId={'Countdown-Timer'}
                                context={'global'}
                                type={'html'}
                            />

                            {!isOnline && <OfflineBanner />}

                            <SkipNavContent
                                tabIndex={undefined}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    outline: 0
                                }}
                            >
                                <Box
                                    as="main"
                                    id="app-main"
                                    role="main"
                                    display="flex"
                                    flexDirection="column"
                                    flex="1"
                                    aria-hidden={isHoveringHeader ? 'true' : undefined}
                                >
                                    <OfflineBoundary isOnline={false}>{children}</OfflineBoundary>
                                </Box>
                            </SkipNavContent>

                            {!isCheckout && !isCustomHeader ? (
                                <Footer oneTrust={oneTrust} openCountryModal={openCountryModal} />
                            ) : !isCheckout && isCustomHeader ? (
                                <CustomFooter />
                            ) : (
                                <CheckoutFooter />
                            )}

                            <AuthModal {...authModal} />
                        </Box>
                    </CurrencyProvider>
                </IntlProvider>
                {ACTIVE_DATA_ENABLED && (
                    <script
                        type="text/javascript"
                        src={getAssetUrl('static/dwanalytics-22.2.js')}
                        id="dwanalytics"
                        async="async"
                        onLoad={trackPage}
                    ></script>
                )}
                {ACTIVE_DATA_ENABLED && (
                    <script
                        src={getAssetUrl('static/dwac-21.7.js')}
                        type="text/javascript"
                        id="dwac"
                        async="async"
                    ></script>
                )}
            </StorefrontPreview>
            <CookieConsent />
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

/**
 * a hook that parallelly and individually fetches category based on the given ids
 * @param ids - list of categories ids to fetch
 * @param queryOptions -  react query options
 * @return list of react query results
 */
export const useCategoryBulk = (ids = [], queryOptions) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {
        parameters: {organizationId}
    } = commerceAPI
    const {site} = useMultiSite()

    const queries = ids.map((id) => {
        return {
            queryKey: queryKeyHelpers.getCategory.queryKey({
                id,
                levels: 2,
                organizationId,
                siteId: site.id
            }),
            queryFn: async () => {
                const token = await getTokenWhenReady()
                const res = await api.shopperProducts.getCategory({
                    parameters: {id, levels: 2},
                    headers: {
                        authorization: `Bearer ${token}`
                    }
                })
                return res
            },
            ...queryOptions,
            enabled: queryOptions.enabled !== false && Boolean(id)
        }
    })
    const res = useQueries({queries})
    return res
}

export default App
